import {
  Button,
  Colors,
  Icon,
  Menu,
  MenuDivider,
  MenuItem,
  Popover,
  PopoverInteractionKind,
  Position
} from '@blueprintjs/core'
import { lang } from 'botpress/shared'
import cx from 'classnames'
import CryptoJS from 'crypto-js'
import React, { FC, useEffect, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux'

import { AppState } from '~/app/rootReducer'
import { useLogout } from '~/app/routes/contexts/LogoutContext'
import BasicAuthentication from '~/auth/basicAuth'
import ChangeLanguage from '~/user/ChangeLanguage'
import { aesKeyStore, jwtKeyStore, formDataStore, subDataStore } from '../../token-store'
import BotsDashboard from '../workspace/bots/BotsDashboard'
import Enquiry from './Enquiry'
import PrivacyAgreement from './PrivacyAgreement'
import { fetchProfile } from './reducer'
import style from './style.scss'
import UpdatePassword from './UpdatePassword'
import UserProfile from './UpdateUserProfile'
import UserActivity from './UserActivity'
import VoiceRecorder from './VoicetoText'

type Props = ConnectedProps<typeof connector>

const UTIL_URL = process.env.REACT_APP_UTIL_URL || 'http://localhost:7001/utils' //'https://www.app.xmati.ai/utils'
const SECRET_KEY = process.env.REACT_APP_CRYPTO_SECRET || ''

const UserDropdownMenu: FC<Props> = props => {
  const { logoutSilently } = useLogout()
  // const [isGemini, setGemini] = useState(false)
  const [isProfileOpen, setProfileOpen] = useState(false)
  const [isPasswordOpen, setPasswordOpen] = useState(false)
  const [isLanguageOpen, setLanguageOpen] = useState(false)
  const [isPrivacyOpen, setPrivacyOpen] = useState(false)
  const [isEnquiryOpen, setEnquiryOpen] = useState(false)
  const [isBotsDashboardOpen, setBotsDashboardOpen] = useState(false)
  const [isExpired, setExpiry] = useState(false)
  const [savedFormData, setSavedFormData] = useState<any>({})
  const [isActivityOpen, setActivityOpen] = useState(false)
  const subData = subDataStore.get() || {}
  const IS_DEMO = subData.subscription === 'Demo'

  // Store references to opened windows
  const subscriptionWindowRef = React.useRef<Window | null>(null)
  const adminWindowRef = React.useRef<Window | null>(null)
  const cardWindowRef = React.useRef<Window | null>(null)


  useEffect(() => {
    !props.profile && props.fetchProfile()

    const formData = formDataStore.get() || {}
    setSavedFormData(formData)

    let expiry
    const subscription = subData?.subscription || 'Trial'
    const daysRemaining = subData.daysRemaining || 0
    const expired = subData.expired || 0

    if (subscription === 'Trial') {
      expiry = expired || false
    } else {
      expiry = expired && daysRemaining <= -4
    }
    setExpiry(expiry)

  }, [])

  const logout = async () => {
    // Call logoutSilently to set the flag before clearing storage
    logoutSilently()

    // Then proceed with normal logout
    const auth: BasicAuthentication = new BasicAuthentication()
    await auth.logout()
  }

  if (!props.profile) {
    return null
  }

  const toggleProfile = () => setProfileOpen(!isProfileOpen)
  const toggleCard = () => {
    openOrFocusWindow('/card-update', 'xmati_card_update_window', cardWindowRef)
  }
  const toggleBotsDashboard = () => setBotsDashboardOpen(!isBotsDashboardOpen)

  // Reusable function to open or focus external window
  const openOrFocusWindow = (
    path: string,
    windowName: string,
    windowRef: React.MutableRefObject<Window | null>
  ) => {
    // Check if window is already open and not closed
    if (windowRef.current && !windowRef.current.closed) {
      windowRef.current.focus()
      return
    }

    // Try to find existing window by name
    try {
      const existingWindow = window.open('', windowName)
      if (existingWindow && existingWindow.location.href.includes(UTIL_URL) && !existingWindow.closed) {
        existingWindow.focus()
        windowRef.current = existingWindow
        return
      }
    } catch (e) {
      // Cross-origin error means window exists but on different domain
    }

    // Prepare data for new window
    const email = formDataStore.get().email
    const token = jwtKeyStore.get() || ''
    const aesKey = aesKeyStore.get() || ''

    const dataObject = { email, token, aesKey }

    // Encrypt the data
    const data = CryptoJS.AES.encrypt(JSON.stringify(dataObject), SECRET_KEY).toString()
    const params = new URLSearchParams({ data })

    // Open new window
    const newWindow = window.open(`${UTIL_URL}${path}?${params.toString()}`, windowName)
    windowRef.current = newWindow

    // Send auth token to new window continuously for 15 seconds (every 1 second)
    let callCount = 0
    const maxCalls = 15

    const sendAuthToken = () => {
      if (newWindow && !newWindow.closed) {
        newWindow.postMessage({
          type: 'AUTH_TOKEN',
          params: params.toString(),
          timestamp: Date.now()
        }, UTIL_URL)
      }
    }

    const intervalId = setInterval(() => {
      if (callCount >= maxCalls || !newWindow || newWindow.closed) {
        clearInterval(intervalId)
        return
      }
      sendAuthToken()
      callCount++
    }, 1000) // Send every 1 second
  }

  const toggleSubscription = () => {
    openOrFocusWindow('/subscription', 'xmati_subscription_window', subscriptionWindowRef)
  }

  const toggleAdminControl = () => {
    openOrFocusWindow('/admin', 'xmati_admin_window', adminWindowRef)
  }
  const togglePassword = () => setPasswordOpen(!isPasswordOpen)
  const toggleLanguage = () => setLanguageOpen(!isLanguageOpen)
  const togglePrivacy = () => setPrivacyOpen(!isPrivacyOpen)
  const toggleEnquiry = () => setEnquiryOpen(!isEnquiryOpen)
  const toggleActivity = () => setActivityOpen(!isActivityOpen)

  const { email, fullName, strategyType, picture_url } = props.profile
  const canChangePassword = strategyType === 'basic'

  const icon = picture_url ? (
    <img src={picture_url} className={cx('dropdown-picture', style.dropdown_picture)} />
  ) : (
    <Icon icon="user" color={Colors.WHITE} />
  )

  return (
    <div>
      <Popover minimal position={Position.BOTTOM} interactionKind={PopoverInteractionKind.CLICK}>
        <Button id="btn-menu-user-dropdown" icon={icon} minimal rightIcon={<Icon color={Colors.WHITE} icon="caret-down" />} />
        <Menu>
          <MenuDivider title={lang.tr('admin.signedInAs', { name: savedFormData.email })} />

          {!IS_DEMO && savedFormData.email !== 'xmatiservice@gmail.com' && (<MenuItem id="btn-subscription" icon="dollar" text={'Subscription'} onClick={toggleSubscription} />)}

          {!isExpired && (<MenuItem id="btn-bots-dashboard" icon="dashboard" text={'Bots Dashboard'} onClick={toggleBotsDashboard} />)}

          {!isExpired && (<MenuItem id="btn-activity" icon="history" text={'Your Activity'} onClick={toggleActivity} />)}

          {/* <MenuItem id="btn-gemini" icon="user" text={'Gemini Speech'} onClick={toggleGemini} /> */}

          {savedFormData.email === 'xmatiservice@gmail.com' && (<MenuItem id="btn-admin-control" icon="user" text={'Admin Control'} onClick={toggleAdminControl} />)}

          {!IS_DEMO && !isExpired && (<MenuItem id="btn-profile" icon="user" text={lang.tr('admin.updateProfile')} onClick={toggleProfile} />)}

          {!IS_DEMO && !isExpired && savedFormData.email !== 'xmatiservice@gmail.com' && (<MenuItem id="btn-card" icon="credit-card" text={'Update Payment Card'} onClick={toggleCard} />)}

          {!IS_DEMO && canChangePassword && !isExpired && (
            <MenuItem id="btn-changepass" icon="key" text={lang.tr('admin.changePassword')} onClick={togglePassword} />
          )}

          <MenuItem id="btn-privacy" icon="document" text={'Privacy Policy'} onClick={togglePrivacy} />

          {savedFormData.email !== 'xmatiservice@gmail.com' && (<MenuItem id="btn-enquiry" icon="help" text={'Submit Enquiry'} onClick={toggleEnquiry} />)}

          {/* {!isExpired && (<MenuItem
            id="btn-changeLanguage"
            icon="translate"
            text={lang.tr('admin.changeLanguage')}
            onClick={toggleLanguage}
          />)} */}

          <MenuDivider />
          <MenuItem id="btn-logout" icon="log-out" text={lang.tr('admin.logout')} onClick={logout} />
        </Menu>
      </Popover>

      <UpdatePassword profile={props.profile} isOpen={isPasswordOpen} toggle={togglePassword} />

      <UserProfile
        isOpen={isProfileOpen}
        toggle={toggleProfile}
        profile={props.profile}
        fetchProfile={props.fetchProfile}
      />



      <PrivacyAgreement
        isOpen={isPrivacyOpen}
        toggle={togglePrivacy}
      />

      <Enquiry
        isOpen={isEnquiryOpen}
        toggle={toggleEnquiry}
      />

      <BotsDashboard
        isOpen={isBotsDashboardOpen}
        toggle={toggleBotsDashboard}
      />

      <UserActivity
        isOpen={isActivityOpen}
        toggle={toggleActivity}
        email={savedFormData.email || props.profile?.email}
      />

      {/* <VoiceRecorder
        isOpen={isGemini}
        toggle={toggleGemini}>
      </VoiceRecorder> */}

      <ChangeLanguage isOpen={isLanguageOpen} toggle={toggleLanguage} />
    </div>
  )
}

const mapStateToProps = (state: AppState) => ({ profile: state.user.profile })
const connector = connect(mapStateToProps, { fetchProfile })

export default connector(UserDropdownMenu)
